import { z } from 'zod';
import { VISIBILITY_VALUES, WORK_ITEM_LINK_CONTEXTS, WORK_ITEM_TYPES, WORK_PRIORITIES, } from '../constants.js';
import { BooleanQuerySchema, PaginationQuerySchema, stringArrayQuery } from './common.js';
export const WorkItemTypeSchema = z.enum(WORK_ITEM_TYPES);
export const WorkPrioritySchema = z.enum(WORK_PRIORITIES);
// Status is open-ended (workflow definitions can add states); validate against a string with min length.
export const WorkStatusSchema = z.string().min(1).max(64);
export const WorkItemLinkContextSchema = z.enum(WORK_ITEM_LINK_CONTEXTS);
/** Cross-org visibility. See VISIBILITY_VALUES in constants.ts. */
export const VisibilityEnum = z.enum(VISIBILITY_VALUES);
export const VisibilityPatchSchema = z.object({ visibility: VisibilityEnum });
/**
 * Custom property values keyed by the property's `key`. Server validates
 * each key against the custom_properties table (entity='work_item') at
 * write time; the value shape per key is enforced there, not here.
 */
const CustomPropertyValuesSchema = z.record(z.string(), z.unknown());
const ExternalLinkSchema = z.object({
    title: z.string().max(200).optional(),
    url: z.string().url().max(2000),
});
/**
 * Strict (CTSK-445) — unknown keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip. Class-fix for the CTSK-427 silent-drop bug (memory
 * feedback_zod_field_name_silent_drop).
 */
export const WorkItemCreateSchema = z.object({
    projectId: z.string(),
    /** Optional external identifier for importer round-trip. Unique per org. */
    externalId: z.string().min(1).max(64).nullable().optional(),
    /**
     * CTSK-92 (2026-07-24) — Optional caller-supplied canonical key for
     * importer round-trip. Format: `{PROJECT_KEY}-{POSITIVE_INT}` (e.g.
     * `COO-27`). When present:
     *  - Server validates the prefix matches the target project's key
     *  - Server atomically bumps `next_work_item_number` to
     *    `GREATEST(current, N + 1)` so future auto-assigned keys skip
     *    past the imported number (prevents collisions on subsequent
     *    non-importer creates)
     *  - Collision with an existing key in the same org returns 409 CONFLICT
     * When absent: server auto-assigns via the counter (unchanged behavior).
     *
     * Regex enforces uppercase alpha prefix + positive integer suffix (no
     * leading zeros); further prefix-matches-project validation is
     * capability-of-the-route, not the schema, since we don't know the
     * target project at parse time.
     */
    key: z.string().min(1).max(24).regex(/^[A-Z][A-Z0-9]*-[1-9][0-9]*$/, 'Must match {PROJECT_KEY}-{POSITIVE_INT}').optional(),
    type: WorkItemTypeSchema.default('task'),
    title: z.string().min(1).max(400),
    description: z.string().max(50_000).optional(),
    status: WorkStatusSchema.optional(), // defaults to project's default
    priority: WorkPrioritySchema.default('medium'),
    assigneeIds: z.array(z.string()).default([]),
    reviewerIds: z.array(z.string()).default([]),
    groupIds: z.array(z.string()).default([]),
    leadId: z.string().nullable().optional(),
    parentId: z.string().nullable().optional(),
    dependencyIds: z.array(z.string()).default([]),
    labels: z.array(z.string()).default([]),
    /** Free-text milestone / cycle / wave / release tag. */
    milestone: z.string().min(1).max(64).nullable().optional(),
    /** External hyperlinks attached to the work item. */
    externalLinks: z.array(ExternalLinkSchema).max(50).default([]),
    customProperties: CustomPropertyValuesSchema.optional(),
    startDate: z.string().datetime().nullable().optional(),
    dueDate: z.string().datetime().nullable().optional(),
    /**
     * Historical timestamps for importers. ALL THREE require
     * `workitems:timestamp_override`; otherwise the server ignores them and
     * uses now() / null. Without this gate any session could rewrite
     * audit-relevant timestamps.
     */
    createdAt: z.string().datetime().optional(),
    updatedAt: z.string().datetime().optional(),
    completedAt: z.string().datetime().nullable().optional(),
    /**
     * Cross-org visibility. Defaults to 'private' (the DB default); explicit
     * 'public' on create is allowed but requires `workitems:share`. Validated
     * at the route layer because Zod can't see the caller's capabilities.
     */
    visibility: VisibilityEnum.optional(),
}).strict();
// .strip() restores Zod's default unknown-key tolerance for PATCH —
// CTSK-445 made Create strict, and .partial()/.extend() would otherwise
// inherit `unknownKeys: "strict"`. Patch semantics deliberately
// unchanged.
export const WorkItemPatchSchema = WorkItemCreateSchema.partial().extend({
    progressPercent: z.number().int().min(0).max(100).optional(),
}).strip();
export const WorkItemListQuerySchema = PaginationQuerySchema.extend({
    q: z.string().optional(),
    projectId: z.string().optional(),
    status: stringArrayQuery(z.string()).optional(),
    priority: stringArrayQuery(WorkPrioritySchema).optional(),
    assigneeId: z.string().optional(),
    parentId: z.string().optional(),
    label: stringArrayQuery(z.string()).optional(),
    /** Filter by external id (exact match). Useful for importer round-trips. */
    externalId: z.string().min(1).max(64).optional(),
    /** Filter by milestone (exact match). */
    milestone: z.string().min(1).max(64).optional(),
    /**
     * Filter by session attribution tag (0.1.45+, CTSK-154 Phase C).
     * Returns work items with any activity row whose `source` matches
     * the given tag. Useful for "what did session X touch" queries
     * without grepping comment bodies. Kebab-case per SESSION_TAG_REGEX.
     */
    source: z.string().min(1).max(64).optional(),
    /**
     * Hide items with status='done'. Server-side narrow so the pagination
     * total reflects the effective filtered set (previously the "Hide done"
     * quick-filter narrowed only the current page client-side, leaving
     * "Showing 1-100 of 822" stale when done items were toggled off).
     * 0.1.51.
     */
    hideDone: BooleanQuerySchema.optional(),
    /**
     * CTSK-92 (2026-07-24) — `key` and `externalId` added as sort options.
     * `key` uses NATURAL numeric sort (regexp_replace to strip the alpha
     * prefix + cast to int) so CTSK-2 sorts before CTSK-10 rather than
     * after (which is what lexicographic ordering would give). Other
     * columns sort by their raw column values. This wiring also fixed a
     * pre-existing bug where the `sort` param was accepted but ignored —
     * the route always ordered by `desc(updatedAt)` regardless of q.sort.
     */
    sort: z.enum(['updatedAt', 'createdAt', 'priority', 'dueDate', 'title', 'key', 'externalId']).default('updatedAt').optional(),
    order: z.enum(['asc', 'desc']).default('desc').optional(),
    includeDeleted: BooleanQuerySchema.optional(),
    /**
     * Filter by the caller's watch state on each work item (#112).
     *   'me'     — only items the caller is watching
     *   'not-me' — only items the caller is NOT watching
     *   (unset)  — no filter (default)
     */
    watchedBy: z.enum(['me', 'not-me']).optional(),
    /**
     * FR 2026-05-29 — cross-org list. When set to 'all-my-orgs' AND
     * combined with watchedBy=me, the route fans out across every org
     * the caller is a member of (instead of the default active-org
     * scope), and stamps each row with its org. Other combinations are
     * rejected as 422 — cross-org list is only for caller-scoped
     * filters like watching.
     */
    scope: z.enum(['active-org', 'all-my-orgs']).optional(),
    /**
     * Dashboard deep-link flags. Each narrows the result set to one of
     * the derived "attention" buckets the API already stamps on each row
     * (isPastDue, isBlocking, needsAttention). Server-side so the
     * narrow happens BEFORE pagination — without these, the dashboard
     * KPI cards could deep-link to a page that contained the right slice
     * in aggregate but didn't show the matching rows on page 1.
     *
     *   'overdue'   — `due_date IS NOT NULL AND due_date <= now() AND status NOT IN (closed states)`
     *   'blocking'  — id is in any other open item's dependency_ids[]
     *   'attention' — status IN ('blocked','needs_iteration','waiting_on_decision')
     */
    flag: z.enum(['overdue', 'blocking', 'attention']).optional(),
    /**
     * Filter by the work item's linked-document state. Useful for operator
     * triage ("which items don't have a spec attached yet" / "show me only
     * items with reference material").
     *   'with'    — work item has at least one row in document_work_item_links
     *   'without' — work item has zero rows in document_work_item_links
     *   (unset)   — no filter (default)
     *
     * EXISTS / NOT EXISTS subquery against document_work_item_links so we
     * don't have to GROUP BY the whole row set; cheap because of the
     * `doc_work_links_work_idx` index on work_item_id.
     */
    hasDocs: z.enum(['with', 'without']).optional(),
    /**
     * CTSK-243 (re-shipped 2026-08-18) — the four quick filters that were
     * client-only pre-CTSK-243 and needed to move server-side so the header
     * count + rendered rows + CSV export all agreed on the same slice.
     *
     * Originally in the CTSK-243 CHANGELOG but never actually landed in the
     * shared package — CTSK-388's refactor of this schema block dropped them.
     * Re-added here + rewired in `apps/api/src/routes/work-items.ts` in the
     * same commit so the failing count-divergence integration test passes.
     *
     *   assignee='unassigned' — items whose assigneeIds jsonb array is empty
     *   stale                 — items whose updatedAt is older than STALE_WORKITEM_DAYS
     *   dateFrom              — items whose updatedAt >= <ISO 8601 date>
     *   dateTo                — items whose updatedAt <= <ISO 8601 date>
     */
    assignee: z.literal('unassigned').optional(),
    stale: BooleanQuerySchema.optional(),
    dateFrom: z.string().datetime({ offset: true }).optional(),
    dateTo: z.string().datetime({ offset: true }).optional(),
});
export const TransitionSchema = z.object({
    to: WorkStatusSchema,
    note: z.string().max(2000).optional(),
});
/**
 * Strict (CTSK-445) — unknown keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip. Class-fix for the CTSK-427 silent-drop bug (memory
 * feedback_zod_field_name_silent_drop).
 */
export const CommentCreateSchema = z.object({
    body: z.string().min(1).max(50_000),
    /**
     * Override the comment author. Returns 403 if caller lacks
     * `workitems:author_override`. Strict (not silent-ignore) because
     * comment attribution is audit-relevant. Used by importers to
     * preserve historic authors.
     */
    authorId: z.string().min(1).max(26).optional(),
    /**
     * Override the comment timestamp. Silently ignored without
     * `workitems:timestamp_override` so forward-compatible clients can
     * submit the same shape regardless. Used by importers for historic
     * comment streams.
     */
    createdAt: z.string().datetime().optional(),
    /**
     * Optional foreign-source identifier. Scoped per work item (unique).
     * Re-posting a comment with an existing externalId on the same work
     * item returns the existing row instead of duplicating — gives
     * importers the same idempotency story as work items.
     */
    externalId: z.string().min(1).max(64).optional(),
    /**
     * 0.1.46 Phase F — bind existing workitem-attachment documents to
     * this comment. Every id must already appear in
     * `documentWorkItemLinks(workItemId=<this>, context='attachment')`;
     * ids that don't validate are rejected with 422 rather than silently
     * dropped. Use POST /workitems/:id/attachments first to create the
     * attachment, then reference its returned id here. Max 20 per
     * comment (soft cap — bump when a real workflow needs more).
     */
    attachmentDocumentIds: z.array(z.string().min(1).max(64)).max(20).optional(),
}).strict();
/**
 * CTSK-372 — body for PATCH /workitems/:id/comments/:commentId.
 * Body-only mutation by design (non-goals: no version history, no
 * edit-window enforcement, no attribution rewriting) — everything else
 * on the comment (author, timestamps, reactions, attachments) is
 * immutable via this route. min(1) after trim (not raw min-length) so
 * a whitespace-only save 422s instead of silently persisting blank
 * text; server re-validates post-trim.
 */
export const CommentUpdateSchema = z.object({
    // Schema validates SHAPE + size only (400 on non-string / oversize).
    // The "empty after trim → 422" business rule lives in the route
    // (`apps/api/src/routes/work-items.ts` PATCH handler) so operators
    // get UNPROCESSABLE with a clear message instead of a generic
    // Zod validation error. Original CommentUpdateSchema had
    // `.trim().min(1)` here which fired at parse-layer as 400 — that
    // contradicted the docstring's stated intent and broke the
    // comment-edit integration test's `d. empty / whitespace-only body
    // → 422` case.
    body: z.string().max(50_000),
});
/**
 * 0.1.46 Phase B — body for POST /workitems/:id/attachments.
 *
 * JSON form (Phase B ships this only — multipart file upload lands in
 * Phase D). Server creates:
 *   - documents row (content = bodyText, storagePath = attachment:sentinel)
 *   - documentContainers link to `projects-<project-key-lowercase>`
 *     (auto-provisioned with is_workitem_scope=true if missing)
 *   - documentWorkItemLinks(workItemId, context='attachment', note=description)
 *   - work_item_activity(type='attachment')
 *
 * The `filename` field is stored in `documents.original_filename` and is
 * what the file-viewer downloads as; if omitted the server derives one
 * from title + mimeType (e.g. `my-report.md`).
 */
/**
 * Strict (CTSK-445) — unknown keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip. Class-fix for the CTSK-427 silent-drop bug (memory
 * feedback_zod_field_name_silent_drop).
 */
export const WorkItemAttachmentCreateSchema = z.object({
    title: z.string().min(1).max(400),
    /** Operator note attached to the documentWorkItemLinks row. */
    description: z.string().max(2000).nullable().optional(),
    /**
     * Text body of the attachment (markdown, plain text, JSON, etc.).
     * Stored on `documents.content` — no on-disk file for text
     * attachments. Multipart binary uploads land in Phase D.
     */
    bodyText: z.string().min(1).max(1_000_000),
    /** Defaults to `text/markdown` on the server if omitted. */
    mimeType: z.string().min(1).max(200).optional(),
    /** Optional download filename override. Defaults derived from title. */
    filename: z.string().min(1).max(400).optional(),
    /**
     * Optional foreign-source identifier for importer round-trip. Not
     * validated for uniqueness yet — future FR: add unique index scoped
     * per workitem so re-posting is idempotent.
     */
    externalId: z.string().min(1).max(64).optional(),
}).strict();
// ─── Work-item links ─────────────────────────────────────────────────────
/**
 * Strict (CTSK-445) — unknown keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip. Class-fix for the CTSK-427 silent-drop bug (memory
 * feedback_zod_field_name_silent_drop).
 */
export const WorkItemLinkCreateSchema = z.object({
    /** Target work-item id (NOT the source — that comes from the URL). */
    workItemId: z.string().min(1).max(26),
    context: WorkItemLinkContextSchema,
    note: z.string().max(2000).nullable().optional(),
}).strict();
// ─── Bulk import ─────────────────────────────────────────────────────────
/**
 * Strict (CTSK-445) — unknown keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip (items[] entries are strict too via WorkItemCreateSchema).
 * Class-fix for the CTSK-427 silent-drop bug (memory
 * feedback_zod_field_name_silent_drop).
 */
export const WorkItemBulkCreateSchema = z.object({
    items: z.array(WorkItemCreateSchema).min(1).max(500),
    /**
     * If true, the server stops at the first failure and rolls back. Default
     * false: each item is attempted independently and reported in the result.
     */
    stopOnError: z.boolean().default(false),
}).strict();
//# sourceMappingURL=work-item.js.map